import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { ReactNode } from 'react';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { useIntlContext } from '@sorare/core/src/contexts/intl';

import { LeaderboardComposable } from 'components/leaderboard/LeaderboardComposable';
import { getComposeTeamRoute } from 'lib/so5';

import { getLeagueTrackSlotsInfo } from '../utils';
import { LeagueTrckSlotCta_so5LeagueTrack } from './__generated__/index.graphql';

type Props = {
  so5LeagueTrack: LeagueTrckSlotCta_so5LeagueTrack;
  selectedIndex: number;
};

export const LeagueTrckSlotCta = ({ so5LeagueTrack, selectedIndex }: Props) => {
  const { formatMessage } = useIntlContext();

  const { nbContenders: firstEmptySlot } =
    getLeagueTrackSlotsInfo(so5LeagueTrack);

  const { entrySo5Leaderboard, mySo5LeaderboardContenders, completed } =
    so5LeagueTrack;

  const selectedContender = mySo5LeaderboardContenders.at(selectedIndex);

  let label: ReactNode;
  let locked = false;
  if (completed) {
    label = formatMessage({
      id: 'leagueTrck.LeagueTrckSlots.alreadyCompletedTutorial',
      defaultMessage: 'You already completed this tutorial',
    });
  } else if (selectedContender?.so5Lineup) {
    label = formatMessage({
      id: 'leagueTrck.LeagueTrckSlots.editMyTeam',
      defaultMessage: 'Edit my Team',
    });
  } else if (selectedContender?.managerTeam?.hidden) {
    label = formatMessage({
      id: 'leagueTrck.LeagueTrckSlots.reactiveMyTeam',
      defaultMessage: 'Re-activate Team',
    });
  } else if (selectedContender?.managerTeam) {
    label = formatMessage({
      id: 'leagueTrck.LeagueTrckSlots.setMyLineup',
      defaultMessage: 'Set my Lineup',
    });
  } else if (
    firstEmptySlot === selectedIndex &&
    entrySo5Leaderboard.canCompose.value
  ) {
    label = formatMessage({
      id: 'leagueTrck.LeagueTrckSlots.addATeam',
      defaultMessage: 'Add a Team',
    });
  } else {
    locked = true;
    label = entrySo5Leaderboard.canCompose.value ? (
      formatMessage({
        id: 'leagueTrck.LeagueTrckSlots.createPriorTeamsFirst',
        defaultMessage: 'Create prior teams first',
      })
    ) : (
      <LeaderboardComposable
        labelOnly
        so5Leaderboard={entrySo5Leaderboard}
        so5LeagueTrack={so5LeagueTrack}
      />
    );
  }

  const disabled = completed || locked;

  return (
    <Button
      fullWidth
      size="large"
      color={selectedContender?.so5Lineup ? 'secondary' : 'primary'}
      disabled={disabled}
      to={
        !disabled
          ? getComposeTeamRoute({
              leaderboardSlug:
                selectedContender?.so5Leaderboard.slug ||
                entrySo5Leaderboard.slug,
              lineupId: selectedContender?.so5Lineup
                ? selectedContender.so5Lineup.id
                : undefined,
              managerTeamId: selectedContender?.managerTeam
                ? selectedContender.managerTeam.id
                : undefined,
              sport: entrySo5Leaderboard.so5Fixture.sport,
              eventType: entrySo5Leaderboard.so5Fixture.type.toLowerCase(),
            })
          : undefined
      }
    >
      {label}
    </Button>
  );
};

LeagueTrckSlotCta.fragments = {
  so5LeagueTrack: gql`
    fragment LeagueTrckSlotCta_so5LeagueTrack on So5LeagueTrack {
      slug
      entrySo5Leaderboard {
        slug
        canCompose {
          value
        }
        so5Fixture {
          slug
          type
          sport
        }
        ...LeaderboardComposable_so5Leaderboard
      }
      completed
      mySo5LeaderboardContenders(includeHiddenManagerTeam: true) {
        slug
        so5Lineup {
          id
        }
        so5Leaderboard {
          slug
        }
        managerTeam {
          id
          hidden
        }
      }
      ...getLeagueTrackSlotsInfo_so5LeagueTrack
      ...LeaderboardComposable_so5LeagueTrack
    }
    ${LeaderboardComposable.fragments.so5Leaderboard}
    ${LeaderboardComposable.fragments.so5LeagueTrack}
    ${getLeagueTrackSlotsInfo.fragments.so5LeagueTrack}
  ` as TypedDocumentNode<LeagueTrckSlotCta_so5LeagueTrack>,
};
